import DSGsubscribe from "@DSG/Handlers/DSGSubscribe";
import colorPallete, { bodyFonts, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    title = "title",
    description = "description",
    placeholder = "placeholder",
    disclaimer = "disclaimer",
    alignment = "alignment",
    customCSS = "customCSS",
    image = "image",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.label]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.disclaimer]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.image]: string;
    };
}

const mapDispatchToProps = {
    DSGsubscribe,

};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    leftContainer: GridContainerProps;
    rightContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: GridItemProps;
    disclaimerText?: TypographyPresentationProps;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        isFullWidth: true,
        gap: 0,
    },
    leftContainer: {
        css: css`
            flex: 1;
        `,
    },
    rightContainer: {
        css: css`
            flex: 1.36;
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleLabel: {
        variant: "h2",
        size: headlineFonts.headline2SemiBoldLg.fontSize,
        weight: headlineFonts.headline2SemiBoldLg.fontWeight,
        lineHeight: headlineFonts.headline2SemiBoldLg.lineHeight,
        css: css`
            color: ${colorPallete.white};
            margin-bottom: 8px;
            @media screen and (max-width: 768px) {
                font-size: ${headlineFonts.headline3SemiBoldSm.fontSize};
                font-weight: ${headlineFonts.headline3SemiBoldSm.fontWeight};
                line-height: ${headlineFonts.headline3SemiBoldSm.lineHeight};
            }
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    descriptionText: {
        css: css`
            p {
                font-size: ${bodyFonts.body2RegularLg.fontSize};
                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                font-weight: ${bodyFonts.body2RegularLg.fontWeight};
                margin-bottom: 0;
                @media screen and (max-width: 768px) {
                    font-size: ${bodyFonts.body2RegularSm.fontSize};
                    line-height: ${bodyFonts.body2RegularSm.lineHeight};
                    font-weight: ${bodyFonts.body2RegularSm.fontWeight};
                }
            }
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            width: 100%;
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                max-width: 445px;
                margin-right: 24px;
                @media screen and (max-width: 768px) {
                    margin-right: 16px;
                    max-width: 208px;
                }
                > div {
                    margin-top: 0;
                }
                [class*="FormInputWrapper-"] input {
                    border-radius: 64px;
                    border-color: ${colorPallete.grey400};
                    padding: 8px 8px 8px 24px;
                    min-height: 48px;
                    color: ${colorPallete.grey800};
                    &:focus {
                        border-radius: 64px;
                        padding: 8px 8px 8px 24px;
                        border-width: 2px;
                        color: ${colorPallete.grey600};
                    }
                    @media screen and (max-width: 768px) {
                        min-height: 40px;
                    }

                    &[aria-invalid="true"] {
                        border-radius: 64px;
                        border-color: ${colorPallete.semanticRed500} !important;
                        padding: 8px 8px 8px 24px;

                        &:focus {
                            border-radius: 64px;
                            padding: 8px 8px 8px 24px;
                        }
                    }

                    + div {
                        span {
                            color: ${colorPallete.white};
                            font-size: ${labelFonts.label1Regular.fontSize};
                            line-height: ${labelFonts.label1Regular.lineHeight};
                        }
                    }
                }
            `,
        },
    },
    emailButton: {
        css: css`
            width: auto;
            &:disabled {
                margin-bottom: 10px;
            }
        `,
    },
    disclaimerGridItem: {
        width: 12,
    },
    disclaimerText: {
        css: css`
            font-size: 12px;
        `,
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

export const CmsSubscribe = ({ fields, DSGsubscribe, id }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const issubscribe = false;

    const styles = useMergeStyles("subscribe", subscribeStyles);

    const backgroundStyles = `
        background-image: url(${fields.image});
    `;
    const clearEmail = React.useCallback(() => {   
        setEmail("");
    }, []);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        DSGsubscribe({
            email,           
            onSuccess: response => {
                if (response != null &&
                    response !== undefined) {                    
                   if(response)
                   {
                    toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
                    clearEmail();
                   }
                   else
                   {
                    toasterContext.addToast({ body: siteMessage("Dsg_Email_Already_Subscribe"), messageType: "danger" });  
                   }
                }
            },
        });
    };
    

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };
   

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };

    const customGridStyles = {
        css: css`
            ${backgroundStyles};
            background-repeat: no-repeat;
            background-size: cover;
            color: ${colorPallete.white};
            width: 100%;
            display: flex;
            padding-top: 48px;
            padding-bottom: 48px;
            @media screen and (max-width: 768px) {
                background-size: cover;
                background-position: center center;
            }
            @media screen and (max-width: 1024px) {
                padding: 32px;
            }
            > div {
                gap: 40px;
                max-width: 1064px;
                margin: 0 auto;
                align-items: center;
                @media screen and (max-width: 768px) {
                    flex-direction: column;
                    gap: 24px;
                }
            }
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <GridContainer className="main-grid-container" {...styles.mainGridContainer} {...customGridStyles}>
                <GridContainer gap={0} {...styles.leftContainer}>
                    <GridItem
                        className="title-grid-item"
                        {...styles.titleGridItem}
                        css={gridItemCss(styles.titleGridItem)}
                    >
                        <Typography className="title" {...styles.titleLabel}>
                            {fields.title}
                        </Typography>
                    </GridItem>
                    <GridItem
                        className="description-grid-item"
                        {...styles.descriptionGridItem}
                        css={gridItemCss(styles.descriptionGridItem)}
                    >
                        <Typography className="description-text" {...styles.descriptionText}>
                            {parse(fields.description, parserOptions)}
                        </Typography>
                    </GridItem>
                </GridContainer>
                <GridContainer gap={0} {...styles.rightContainer}>
                    <GridItem
                        className="email-grid-item"
                        {...styles.emailGridItem}
                        css={gridItemCss(styles.emailGridItem)}
                    >
                        <TextField
                            className="email-text-field"
                            {...styles.emailTextField}
                            id={id}
                            placeholder={fields.placeholder}
                            value={email}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && emailError}
                        />
                        <Button
                            className="email-button btn-primary-dark"
                            {...styles.emailButton}
                            onClick={onSubscribeClick}
                            disabled={isSubmitted && !!emailError}
                        >
                            {fields.label}
                        </Button>
                    </GridItem>
                    <GridItem
                        className="disclaimer-grid-item"
                        {...styles.disclaimerGridItem}
                        css={gridItemCss(styles.disclaimerGridItem)}
                    >
                        <Typography className="disclaimer-text" {...styles.disclaimerText}>
                            {parse(fields.disclaimer, parserOptions)}
                        </Typography>
                    </GridItem>
                </GridContainer>
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.disclaimer,
                displayName: "Disclaimer",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
